import { Classes, Colors } from "@blueprintjs/core";

import giftboxSVG from "@/../../hypetrain/static/img/giftbox.svg";
import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { FadeInBox } from "@/components/pieces/FadeInBox";
import { CustomIcon } from "@/components/pieces/icons/Icons";
import { getUser } from "@/components/session/JwtTokenManager";
import {
  BRAND_PURPLE,
  LIGHTEST_BACKGROUND,
  SIDE_NAV_WIDTH,
  SITE_NAME,
  STANDARD_APP_MAX_WIDTH,
  SUBTEXT_COLOR,
} from "@/css/constants";
import { useReferralsQuery } from "@/graphql";

import { ReferralList } from "./ReferralList";
import { SharingOptions } from "./SharingOptions";

function ReferralsHeader() {
  const { isAgent = false } = getUser() || {};
  return (
    <Box className="bbs" py={[2, 4]} bg={Colors.WHITE}>
      <Box
        className="container"
        maxWidth={STANDARD_APP_MAX_WIDTH}
        m="0 auto"
        bg={Colors.WHITE}
        px={[14, 24]}
      >
        <Flex alignItems="center">
          <CustomIcon icon="gift" iconSize={26} color={BRAND_PURPLE} />
          <Box ml={3}>
            <h1 style={{ fontSize: 28, margin: 0 }}>
              {isAgent ? "Refer Clients" : "Refer Friends"}
            </h1>
          </Box>
        </Flex>
        <Box color={SUBTEXT_COLOR} mt={2}>
          Earn credits by referring {isAgent ? "clients" : "friends"}
        </Box>
      </Box>
    </Box>
  );
}

function ReferralContent() {
  const { data } = useReferralsQuery();
  const user = data?.currentUser;
  const programTerms = user?.isAgent
    ? "When your referral makes a credit purchase, you will get a 10% reward of all credits they purchase for the next 12 months."
    : "When your friend buys at least 1 credit, we'll give both of you an extra 0.5 credit.";

  const isAgent = user?.isAgent || false;
  return (
    <FadeInBox>
      <Box
        className={`${Classes.ELEVATION_1} round-corners`}
        backgroundColor="#FFF"
      >
        <Media greaterThan="xs">
          <Flex
            justifyContent="center"
            alignItems="center"
            p={24}
            style={{
              background: `radial-gradient(
                101.52% 101.52% at 50% -1.52%,
                rgba(255, 255, 255, 0.62) 49.99%,
                rgba(255, 255, 255, 0.2) 50%,
                rgba(255, 255, 255, 0) 100%
              ), #F9F4EA`,
            }}
          >
            <EmbedSvg src={giftboxSVG} width={170} height={120} />
          </Flex>
        </Media>
        <Box py={24} px={[3, 4]}>
          <Box fontSize={22} fontWeight={600}>
            {isAgent ? "Earn more" : "Give Credit, Get Credit"}
          </Box>
          <Box mt={1}>
            <p>
              Share your link and earn credits for each{" "}
              {isAgent ? "client" : "friend"} you refer.
              {isAgent &&
                ` You earn credits even if the client doesn't work directly with you.`}
            </p>
            <p>{programTerms}</p>
            <p>
              By participating, you agree to the{" "}
              {isAgent ? (
                <a
                  href="https://www.notion.so/placement/Exec-Affiliate-Agreement-8eb81555667743aba3d72f47d03dfca1"
                  target="_blank"
                >
                  Affiliate Agreement
                </a>
              ) : (
                <a
                  href="https://www.notion.so/placement/Placement-Referral-Program-Agreement-8e0986f18a064606a368f8bb7ef6d9f2"
                  target="_blank"
                >
                  Referral Program Agreement
                </a>
              )}
              .
            </p>
          </Box>
        </Box>
      </Box>
      <Box
        mt={24}
        py={24}
        px={[3, 4]}
        className={`${Classes.ELEVATION_1} round-corners`}
        backgroundColor="#FFF"
      >
        <SharingOptions user={user} />
      </Box>
      <Box
        mt={24}
        py={24}
        px={[3, 4]}
        className={`${Classes.ELEVATION_1} round-corners`}
        backgroundColor="#FFF"
      >
        <ReferralList user={user} />
      </Box>
    </FadeInBox>
  );
}

export function ReferralsPage() {
  return (
    <Authenticate maxWidth="100%" layout="dashboard">
      <>
        <SEOHead
          title={`Refer a Friend - ${SITE_NAME}`}
          description={`Earn free coaching by referring clients to ${SITE_NAME}`}
          canonicalPath="/referrals"
          image="https://placement-pub.s3.us-east-2.amazonaws.com/sessions-meta.png"
        />
        <Nav />
        <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
          <ReferralsHeader />
          <Box pb={6} bg={LIGHTEST_BACKGROUND}>
            <Box
              className="container"
              maxWidth={STANDARD_APP_MAX_WIDTH}
              m="0 auto"
              px={[3, 24]}
            >
              <Box width={1} mt={4}>
                <ReferralContent />
              </Box>
            </Box>
          </Box>
        </Box>
      </>
    </Authenticate>
  );
}
